/**
 * Utility functions for bankroll management and mismatch detection
 */

/**
 * Check if there's a mismatch between bankroll storage sum and calculated bankroll
 * @param {number} calculatedBankroll - The calculated bankroll from dashboard
 * @param {Array} storageLocations - Array of bankroll storage locations
 * @param {number} tolerance - Tolerance for mismatch (default 0.01)
 * @returns {Object} Object with isMismatch boolean and details
 */
export const checkBankrollMismatch = (calculatedBankroll, storageLocations, tolerance = 0.01) => {
    if (!calculatedBankroll || !storageLocations || !Array.isArray(storageLocations)) {
        return { isMismatch: false, storageSum: 0, calculatedBankroll: 0, difference: 0 };
    }

    const storageSum = storageLocations.reduce((sum, location) => {
        return sum + parseFloat(location.amount || 0);
    }, 0);

    const difference = Math.abs(calculatedBankroll - storageSum);
    const isMismatch = difference > tolerance;

    return {
        isMismatch,
        storageSum,
        calculatedBankroll,
        difference
    };
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

/**
 * Get alert message for bankroll mismatch
 * @param {Object} mismatchData - Data from checkBankrollMismatch
 * @returns {string} Alert message
 */
export const getMismatchAlertMessage = (mismatchData) => {
    const { storageSum, calculatedBankroll, difference } = mismatchData;
    
    return `Bankroll Mismatch Detected! Calculated bankroll: ${formatCurrency(calculatedBankroll)}, Storage sum: ${formatCurrency(storageSum)}. Difference: ${formatCurrency(difference)}. Please verify your bankroll storage locations.`;
}; 